import {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TextField,
    Grid,
    Typography,
    Pagination,
    Box,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Stack,
    Autocomplete,
} from "@mui/material";
import {PageLayout} from "base/layout";
import {EntityTableSortingHead} from "base/entity";
import {AlertError, PaperContainer, TableDownload} from "base/component/presentational";
import {ServiceRequestFormat, NumberUtil} from "base/utilities";
import DataVisualizationService from "../service/DataVisualizationService";
import {ProvinciaService, ConcelloService} from "concello/service";
import {AglomeracionService} from "saneamiento/aglomeracion/service";
import {EDARService} from "saneamiento/service";
import {DomainProvider, useDomain} from "domain/provider";

import useListSearchParams, {textParam, choiceParam, pageParam, arrayParam} from "base/hooks/useListSearchParams";

const optionsLoadingText = (
    <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
    </Box>
);

const withOptionsLoading = (params, loading) => ({
    ...params.InputProps,
    endAdornment: (
        <>
            {loading ? <CircularProgress color="inherit" size={18} sx={{mr: 1}} /> : null}
            {params.InputProps.endAdornment}
        </>
    ),
});

const entityTypes = [
    {value: "concello", label: "Concello"},
    {value: "aglomeracion", label: "Aglomeración"},
    {value: "edar", label: "EDAR"},
    {value: "conexion", label: "Conexión"},
    {value: "bombeo", label: "Bombeo"},
    {value: "tanque", label: "Tanque"},
];

const pageSize = 20;

const populationRangeOptions = [
    {value: "", label: "Calquera poboación"},
    {value: "0-2000", label: "Menos de 2.000 habs."},
    {value: "2000-5000", label: "Entre 2.000 e 5.000 habs."},
    {value: "5000-10000", label: "Entre 5.000 e 10.000 habs."},
    {value: "10000-20000", label: "Entre 10.000 e 20.000 habs."},
    {value: "20000-50000", label: "Entre 20.000 e 50.000 habs."},
    {value: "50000-", label: "Máis de 50.000 habs."},
];

const defaultFilter = {
    search: "",
    provincia: [],
    aglomeracion: [],
    poboacionFixaTotalRange: "",
    poboacionFixaSaneamentoRange: "",
    poboacionTotalRange: "",
    poboacionConectadaRange: "",
    poboacionFixaRange: "",
    poboacionIndustrialRange: "",
    tratamento: "",
    concello: [],
    edar: "",
};
const querySchema = {
    ...Object.fromEntries(Object.entries(defaultFilter).map(([key, value]) => [
        key, Array.isArray(value) ? arrayParam : textParam(value),
    ])),
    entity: choiceParam("concello", entityTypes.map(type => type.value)),
    page: pageParam,
    sort: textParam("codigo"),
    order: choiceParam("asc", ["asc", "desc"]),
};

const DataDashboard = () => {
    const [query, updateQuery] = useListSearchParams(querySchema);
    const {entity: entityType, page, sort, order} = query;
    const filter = useMemo(() => Object.fromEntries(
        Object.keys(defaultFilter).map(key => [key, query[key]])
    ), [query]);
    const setFilter = value => updateQuery({
        ...(typeof value === "function" ? value(filter) : value),
        page: 1,
        sort: "codigo",
        order: "asc",
    });
    const [elements, setElements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);

    const [searchValue, setSearchValue] = useState(filter.search);
    useEffect(() => {
        setSearchValue(filter.search);
    }, [query, filter.search]);

    const [provincias, setProvincias] = useState([]);
    const [loadingProvincias, setLoadingProvincias] = useState(true);
    const [aglomeracions, setAglomeracions] = useState([]);
    const [loadingAglomeracions, setLoadingAglomeracions] = useState(true);
    const [concellos, setConcellos] = useState([]);
    const [loadingConcellos, setLoadingConcellos] = useState(true);
    const [edars, setEdars] = useState([]);
    const [loadingEdars, setLoadingEdars] = useState(true);
    const {dominioedartipotratamento = []} = useDomain() || {};

    useEffect(() => {
        ProvinciaService.getList()
            .then(data => setProvincias(data.results || data))
            .catch(() => setProvincias([]))
            .finally(() => setLoadingProvincias(false));
        AglomeracionService.getList()
            .then(data => setAglomeracions(data.results || data))
            .catch(() => setAglomeracions([]))
            .finally(() => setLoadingAglomeracions(false));
        ConcelloService.getList()
            .then(data => {
                const list = data.results || data;
                if (Array.isArray(list)) {
                    list.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
                }
                setConcellos(list || []);
            })
            .catch(() => setConcellos([]))
            .finally(() => setLoadingConcellos(false));
        EDARService.getList()
            .then(data => {
                const list = data.results || data;
                if (Array.isArray(list)) {
                    list.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
                }
                setEdars(list || []);
            })
            .catch(() => setEdars([]))
            .finally(() => setLoadingEdars(false));
    }, []);

    const requestVersion = useRef(0);

    const fetchData = useCallback(() => {
        const request = ++requestVersion.current;
        setLoading(true);
        let serviceCall;
        const apiFilter = {
            search: filter.search,
        };

        if (entityType === "concello") {
            if (Array.isArray(filter.provincia) && filter.provincia.length > 0) {
                apiFilter.provincia = filter.provincia.join(",");
            } else if (typeof filter.provincia === "string" && filter.provincia) {
                apiFilter.provincia = filter.provincia;
            }
            if (filter.poboacionFixaTotalRange) {
                const [min, max] = filter.poboacionFixaTotalRange.split("-");
                if (min) apiFilter.poboacion_fixa_total_min = min;
                if (max) apiFilter.poboacion_fixa_total_max = max;
            }
            if (filter.poboacionFixaSaneamentoRange) {
                const [min, max] = filter.poboacionFixaSaneamentoRange.split("-");
                if (min) apiFilter.poboacion_fixa_saneamento_min = min;
                if (max) apiFilter.poboacion_fixa_saneamento_max = max;
            }
        }
        if (entityType === "aglomeracion") {
            if (filter.poboacionTotalRange) {
                const [min, max] = filter.poboacionTotalRange.split("-");
                if (min) apiFilter.poboacion_total_min = min;
                if (max) apiFilter.poboacion_total_max = max;
            }
            if (filter.poboacionConectadaRange) {
                const [min, max] = filter.poboacionConectadaRange.split("-");
                if (min) apiFilter.poboacion_conectada_min = min;
                if (max) apiFilter.poboacion_conectada_max = max;
            }
            if (filter.poboacionFixaRange) {
                const [min, max] = filter.poboacionFixaRange.split("-");
                if (min) apiFilter.poboacion_fixa_min = min;
                if (max) apiFilter.poboacion_fixa_max = max;
            }
            if (filter.poboacionIndustrialRange) {
                const [min, max] = filter.poboacionIndustrialRange.split("-");
                if (min) apiFilter.poboacion_industrial_min = min;
                if (max) apiFilter.poboacion_industrial_max = max;
            }
            if (filter.tratamento) {
                apiFilter.tratamento = filter.tratamento;
            }
        }
        if (entityType === "edar") {
            if (Array.isArray(filter.aglomeracion) && filter.aglomeracion.length > 0) {
                apiFilter.aglomeracion = filter.aglomeracion.join(",");
            } else if (typeof filter.aglomeracion === "string" && filter.aglomeracion) {
                apiFilter.aglomeracion = filter.aglomeracion;
            }

            if (Array.isArray(filter.concello) && filter.concello.length > 0) {
                apiFilter.concello = filter.concello.join(",");
            } else if (typeof filter.concello === "string" && filter.concello) {
                apiFilter.concello = filter.concello;
            }
            if (filter.poboacionTotalRange) {
                const [min, max] = filter.poboacionTotalRange.split("-");
                if (min) apiFilter.poboacion_total_min = min;
                if (max) apiFilter.poboacion_total_max = max;
            }
            if (filter.poboacionConectadaRange) {
                const [min, max] = filter.poboacionConectadaRange.split("-");
                if (min) apiFilter.poboacion_conectada_min = min;
                if (max) apiFilter.poboacion_conectada_max = max;
            }
            if (filter.poboacionFixaRange) {
                const [min, max] = filter.poboacionFixaRange.split("-");
                if (min) apiFilter.poboacion_fixa_min = min;
                if (max) apiFilter.poboacion_fixa_max = max;
            }
            if (filter.poboacionIndustrialRange) {
                const [min, max] = filter.poboacionIndustrialRange.split("-");
                if (min) apiFilter.poboacion_industrial_min = min;
                if (max) apiFilter.poboacion_industrial_max = max;
            }
            if (filter.tratamento) {
                apiFilter.tratamento = filter.tratamento;
            }
        }
        if (entityType === "conexion") {
            apiFilter.tipo_orixe = "nucleo";
            apiFilter.tipo_destino = "edar";
            if (Array.isArray(filter.concello) && filter.concello.length > 0) {
                apiFilter.concello_orixe = filter.concello.join(",");
            } else if (typeof filter.concello === "string" && filter.concello) {
                apiFilter.concello_orixe = filter.concello;
            }

            if (Array.isArray(filter.aglomeracion) && filter.aglomeracion.length > 0) {
                apiFilter.aglomeracion = filter.aglomeracion.join(",");
            } else if (typeof filter.aglomeracion === "string" && filter.aglomeracion) {
                apiFilter.aglomeracion = filter.aglomeracion;
            }
            if (filter.edar) {
                apiFilter.id_destino = filter.edar;
            }
        }
        if (entityType === "bombeo" || entityType === "tanque") {
            if (Array.isArray(filter.aglomeracion) && filter.aglomeracion.length > 0) {
                apiFilter.aglomeracion = filter.aglomeracion.join(",");
            } else if (typeof filter.aglomeracion === "string" && filter.aglomeracion) {
                apiFilter.aglomeracion = filter.aglomeracion;
            }

            if (Array.isArray(filter.concello) && filter.concello.length > 0) {
                apiFilter.concello = filter.concello.join(",");
            } else if (typeof filter.concello === "string" && filter.concello) {
                apiFilter.concello = filter.concello;
            }
        }

        switch (entityType) {
            case "concello":
                serviceCall = DataVisualizationService.getConcellos(apiFilter, page, sort, order);
                break;
            case "aglomeracion":
                serviceCall = DataVisualizationService.getAglomeracions(apiFilter, page, sort, order);
                break;
            case "edar":
                serviceCall = DataVisualizationService.getEdars(apiFilter, page, sort, order);
                break;
            case "conexion":
                serviceCall = DataVisualizationService.getConexiones(apiFilter, page, sort, order);
                break;
            case "bombeo":
                serviceCall = DataVisualizationService.getBombeos(apiFilter, page, sort, order);
                break;
            case "tanque":
                serviceCall = DataVisualizationService.getTanques(apiFilter, page, sort, order);
                break;
            default:
                setLoading(false);
                return;
        }

        serviceCall
            .then(data => {
                if (request !== requestVersion.current) return;
                const results = data?.results || data;
                setElements(Array.isArray(results) ? results : []);
                setTotal(data?.count || (Array.isArray(results) ? results.length : 0));
                setLoading(false);
                setError(null);
            })
            .catch(err => {
                if (request !== requestVersion.current) return;
                setElements([]);
                setError(err);
                setLoading(false);
            });
    }, [entityType, filter, page, sort, order]);

    useEffect(() => {
        fetchData();
        return () => {
            // Ignore responses from a page that has already been left.
            requestVersion.current += 1;
        };
    }, [fetchData]);

    const handleEntityTypeChange = e => {
        updateQuery({...defaultFilter, entity: e.target.value, page: 1, sort: "codigo", order: "asc"});
    };

    const handleRequestSort = (event, property) => {
        const isAsc = sort === property && order === "asc";
        updateQuery({sort: property, order: isAsc ? "desc" : "asc"});
    };

    const getColumns = () => {
        switch (entityType) {
            case "concello":
                return [
                    {id: "codigo", label: "Código", key: "codigo"},
                    {id: "nome", label: "Nome", key: "nome"},
                    {
                        id: "cargaurbana",
                        label: "Poboación Total: Rede > Urbana",
                        key: "cargaurbana",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargadomestica",
                        label: "Poboación Total: Rede > Doméstica",
                        key: "cargadomestica",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargaurbanatotal",
                        label: "Poboación Total: Concello > Urbana",
                        key: "cargaurbanatotal",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargadomesticatotal",
                        label: "Poboación Total: Concello > Doméstica",
                        key: "cargadomesticatotal",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargadomesticafixa",
                        label: "Poboación Doméstica: Rede > Fixa",
                        key: "cargadomesticafixa",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargadomesticafixatotal",
                        label: "Poboación Doméstica: Concello > Fixa",
                        key: "cargadomesticafixatotal",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "carganondomesticaindustrial",
                        label: "Non Doméstica: Rede > Industrial",
                        key: "carganondomesticaindustrial",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "carganondomesticaindustrialtotal",
                        label: "Non Doméstica: Concello > Industrial",
                        key: "carganondomesticaindustrialtotal",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "lonxitudetotalcolector",
                        label: "Inventario Total: Lonxitude Rede",
                        key: "lonxitudetotalcolector",
                        format: val => NumberUtil.formatDecimal(val, 3),
                    },
                    {
                        id: "bombeos",
                        label: "Inventario Bombelos: Totais",
                        key: "bombeos",
                        format: val => NumberUtil.formatInteger(val),
                    },
                    {
                        id: "tanques",
                        label: "Inventario Tanques: Totais",
                        key: "tanques",
                        format: val => NumberUtil.formatInteger(val),
                    },
                    {
                        id: "edartratamento0",
                        label: "Inventario EDAR: Tratamento deficiente",
                        key: "edartratamento0",
                        format: val => NumberUtil.formatInteger(val),
                    },
                    {
                        id: "edartratamento1",
                        label: "Inventario EDAR: Tratamento primario",
                        key: "edartratamento1",
                        format: val => NumberUtil.formatInteger(val),
                    },
                    {
                        id: "edartratamento2",
                        label: "Inventario EDAR: Tratamento secundario",
                        key: "edartratamento2",
                        format: val => NumberUtil.formatInteger(val),
                    },
                    {
                        id: "edartratamento3",
                        label: "Inventario EDAR: Tratamento avanzado",
                        key: "edartratamento3",
                        format: val => NumberUtil.formatInteger(val),
                    },
                ];
            case "aglomeracion":
                return [
                    {id: "codigo", label: "Código", key: "codigo"},
                    {id: "nome", label: "Nome", key: "nome"},
                    {
                        id: "ente_publico_representativo_label",
                        label: "EPR (Ente público representativo)",
                        key: "ente_publico_representativo_label",
                    },
                    {
                        id: "administracion_publica_responsable_label",
                        label: "APRS",
                        key: "administracion_publica_responsable_label",
                    },
                    {
                        id: "cargatotal",
                        label: "Poboación Total",
                        key: "cargatotal",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargaconectadatotal",
                        label: "Poboación Conectada Total",
                        key: "cargaconectadatotal",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargaurbana",
                        label: "Poboación conectada Urbana",
                        key: "cargaurbana",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargadomestica",
                        label: "Poboación Conectada Total Doméstica",
                        key: "cargadomestica",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "cargadomesticafixa",
                        label: "Poboación Fixa (Poboación Conectada Domestica Fixa)",
                        key: "cargadomesticafixa",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "carganondomesticaindustrial",
                        label: "Poboación Industrial (Poboación Conectada Non Doméstica Industrial)",
                        key: "carganondomesticaindustrial",
                        format: val => NumberUtil.formatDecimal(val, 0),
                    },
                    {
                        id: "tratamento_label",
                        label: "Tratamento EDAR",
                        key: "tratamento_label",
                    },
                    {
                        id: "lonxitudecolector",
                        label: "Lonxitude rede (inventario Total)",
                        key: "lonxitudecolector",
                        format: val => NumberUtil.formatDecimal(val, 3),
                    },
                    {
                        id: "bombeos",
                        label: "Bombeos Totais",
                        key: "bombeos",
                        format: val => NumberUtil.formatInteger(val),
                    },
                    {
                        id: "tanques",
                        label: "Tanques Totais",
                        key: "tanques",
                        format: val => NumberUtil.formatInteger(val),
                    },
                ];
            case "edar":
                return [
                    {id: "codigo", label: "Código", key: "codigo"},
                    {id: "nome", label: "Nome", key: "nome"},
                    {id: "aglomeracion_label", label: "Aglomeración", id_sort: "aglomeracion__nome", key: "aglomeracion_label"},
                    {id: "concello_label", label: "Concello", id_sort: "concello__nome", key: "concello_label"},
                    {id: "tipo_carga_label", label: "Tipo de carga", id_sort: "tipo_carga", key: "tipo_carga_label"},
                    {id: "urbanatotal", label: "Poboación conectada", key: "urbanatotal", format: val => NumberUtil.formatDecimal(val, 0)},
                    {id: "cargadomestica", label: "Poboación doméstica", key: "cargadomestica", format: val => NumberUtil.formatDecimal(val, 0)},
                    {id: "cargadomesticafixa", label: "Poboación fixa", key: "cargadomesticafixa", format: val => NumberUtil.formatDecimal(val, 0)},
                    {id: "nondomesticaindustrial", label: "Poboación industrial", key: "nondomesticaindustrial", format: val => NumberUtil.formatDecimal(val, 0)},
                    {id: "tipo_tratamento_label", label: "Tratamento", key: "tipo_tratamento_label"},
                    {id: "tratamento_avanzado_label", label: "Tratamento avanzado", key: "tratamento_avanzado_label"},
                    {id: "ano_posta_marcha", label: "Ano posta en marcha", key: "ano_posta_marcha", format: val => NumberUtil.formatInteger(val)},
                    {id: "habitantes_equivalentes_desenho", label: "Habitantes equivalentes", key: "habitantes_equivalentes_desenho", format: val => NumberUtil.formatInteger(val)},
                ];
            case "conexion":
                return [
                    {id: "concello_orixe_label", label: "Concello", id_sort: "concello_orixe__nome", key: "concello_orixe_label"},
                    {id: "codigo_orixe", label: "Código núcleo", key: "codigo_orixe"},
                    {id: "nome_orixe", label: "Nome do núcleo", key: "nome_orixe"},
                    {id: "poboacion_fixa", label: "Poboación fixa", key: "poboacion_fixa", format: val => NumberUtil.formatDecimal(val, 0)},
                    {id: "poboacion_total", label: "Poboación total", key: "poboacion_total", format: val => NumberUtil.formatDecimal(val, 0)},
                    {id: "codigo_destino", label: "Código EDAR", key: "codigo_destino"},
                    {id: "nome_destino", label: "Nome EDAR", key: "nome_destino"},
                    {id: "porcentaxe", label: "Porcentaxe conectado a EDAR", key: "porcentaxe", format: val => NumberUtil.formatDecimal(val, 2)},
                    {id: "aglomeracion_label", label: "Aglomeración", key: "aglomeracion_label"},
                ];
            case "bombeo":
                return [
                    {id: "codigo", label: "Código", key: "codigo"},
                    {id: "nome", label: "Nome", key: "nome"},
                    {id: "caudal_bombeo", label: "Caudal", key: "caudal_bombeo", format: val => NumberUtil.formatDecimal(val, 2)},
                    {id: "altura_bombeo", label: "Altura", key: "altura_bombeo", format: val => NumberUtil.formatDecimal(val, 2)},
                    {id: "aglomeracion_label", label: "Aglomeración", id_sort: "aglomeracion_label", key: "aglomeracion_label"},
                    {id: "concello_label", label: "Concello", id_sort: "concello_label", key: "concello_label"},
                ];
            case "tanque":
                return [
                    {id: "codigo", label: "Código", key: "codigo"},
                    {id: "nome", label: "Nome", key: "nome"},
                    {id: "capacidade", label: "Capacidade", key: "capacidade", format: val => NumberUtil.formatDecimal(val, 2)},
                    {id: "aglomeracion_label", label: "Aglomeración", id_sort: "aglomeracion_label", key: "aglomeracion_label"},
                    {id: "concello_label", label: "Concello", id_sort: "concello_label", key: "concello_label"},
                ];
            default:
                return [];
        }
    };

    const columns = getColumns();

    return (
        <PageLayout>
            <PaperContainer>
                <Typography variant="h5" sx={{mb: 3, fontWeight: "bold"}}>
                    Visualización de Datos
                </Typography>

                <AlertError error={error} />

                <Stack sx={{width: "100%", bgcolor: "#ffffff", p: 2.5, mb: 4, borderRadius: 2, border: "1px solid #e0e0e0"}}>
                    <Typography variant="h6" sx={{mb: 2, fontWeight: "bold"}}>Filtros</Typography>
                    <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap" className="wrap-stack-fix">
                        <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo de Entidade</InputLabel>
                                <Select
                                    value={entityType}
                                    label="Tipo de Entidade"
                                    onChange={handleEntityTypeChange}
                                >
                                    {entityTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                            <TextField
                                fullWidth
                                label="Buscar"
                                variant="outlined"
                                size="small"
                                value={searchValue}
                                onChange={e => setSearchValue(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        setFilter(prev => ({ ...prev, search: searchValue }));
                                    }
                                }}
                                placeholder="Código ou nome..."
                            />
                        </Box>
                        {entityType === "concello" && (
                            <>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingProvincias}
                                        loadingText={optionsLoadingText}
                                        options={provincias}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={provincias.filter(p => (Array.isArray(filter.provincia) ? filter.provincia : []).includes(String(p.id)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                provincia: newValue.map(p => p.id)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Provincia" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingProvincias)} />
                                        )}
                                        noOptionsText={loadingProvincias ? optionsLoadingText : "Non se atoparon provincias"}
                                    />
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Fixa Total</InputLabel>
                                        <Select
                                            value={filter.poboacionFixaTotalRange}
                                            label="Poboación Fixa Total"
                                            onChange={e => setFilter({...filter, poboacionFixaTotalRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Fixa Saneamento</InputLabel>
                                        <Select
                                            value={filter.poboacionFixaSaneamentoRange}
                                            label="Poboación Fixa Saneamento"
                                            onChange={e => setFilter({...filter, poboacionFixaSaneamentoRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                        {entityType === "aglomeracion" && (
                            <>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Total</InputLabel>
                                        <Select
                                            value={filter.poboacionTotalRange}
                                            label="Poboación Total"
                                            onChange={e => setFilter({...filter, poboacionTotalRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Conectada</InputLabel>
                                        <Select
                                            value={filter.poboacionConectadaRange}
                                            label="Poboación Conectada"
                                            onChange={e => setFilter({...filter, poboacionConectadaRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Fixa</InputLabel>
                                        <Select
                                            value={filter.poboacionFixaRange}
                                            label="Poboación Fixa"
                                            onChange={e => setFilter({...filter, poboacionFixaRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Industrial</InputLabel>
                                        <Select
                                            value={filter.poboacionIndustrialRange}
                                            label="Poboación Industrial"
                                            onChange={e => setFilter({...filter, poboacionIndustrialRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Tratamento</InputLabel>
                                        <Select
                                            value={filter.tratamento}
                                            label="Tratamento"
                                            onChange={e => setFilter({...filter, tratamento: e.target.value})}
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            {dominioedartipotratamento.map(t => (
                                                <MenuItem key={t.value} value={t.value}>
                                                    {t.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                        {entityType === "edar" && (
                            <>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingAglomeracions}
                                        loadingText={optionsLoadingText}
                                        options={aglomeracions}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={aglomeracions.filter(a => (Array.isArray(filter.aglomeracion) ? filter.aglomeracion : []).includes(String(a.id)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                aglomeracion: newValue.map(a => a.id)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Aglomeración" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingAglomeracions)} />
                                        )}
                                        noOptionsText={loadingAglomeracions ? optionsLoadingText : "Non se atoparon aglomeracións"}
                                    />
                                </Box>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingConcellos}
                                        loadingText={optionsLoadingText}
                                        options={concellos}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={concellos.filter(c => (Array.isArray(filter.concello) ? filter.concello : []).includes(String(c.codigo)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                concello: newValue.map(c => c.codigo)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Concello" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingConcellos)} />
                                        )}
                                        noOptionsText={loadingConcellos ? optionsLoadingText : "Non se atoparon concellos"}
                                    />
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Total</InputLabel>
                                        <Select
                                            value={filter.poboacionTotalRange}
                                            label="Poboación Total"
                                            onChange={e => setFilter({...filter, poboacionTotalRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Conectada</InputLabel>
                                        <Select
                                            value={filter.poboacionConectadaRange}
                                            label="Poboación Conectada"
                                            onChange={e => setFilter({...filter, poboacionConectadaRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Fixa</InputLabel>
                                        <Select
                                            value={filter.poboacionFixaRange}
                                            label="Poboación Fixa"
                                            onChange={e => setFilter({...filter, poboacionFixaRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Poboación Industrial</InputLabel>
                                        <Select
                                            value={filter.poboacionIndustrialRange}
                                            label="Poboación Industrial"
                                            onChange={e => setFilter({...filter, poboacionIndustrialRange: e.target.value})}
                                        >
                                            {populationRangeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Tratamento</InputLabel>
                                        <Select
                                            value={filter.tratamento}
                                            label="Tratamento"
                                            onChange={e => setFilter({...filter, tratamento: e.target.value})}
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            {dominioedartipotratamento.map(t => (
                                                <MenuItem key={t.value} value={t.value}>
                                                    {t.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                        {entityType === "conexion" && (
                            <>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingConcellos}
                                        loadingText={optionsLoadingText}
                                        options={concellos}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={concellos.filter(c => (Array.isArray(filter.concello) ? filter.concello : []).includes(String(c.codigo)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                concello: newValue.map(c => c.codigo)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Concello do núcleo" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingConcellos)} />
                                        )}
                                        noOptionsText={loadingConcellos ? optionsLoadingText : "Non se atoparon concellos"}
                                    />
                                </Box>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingAglomeracions}
                                        loadingText={optionsLoadingText}
                                        options={aglomeracions}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={aglomeracions.filter(a => (Array.isArray(filter.aglomeracion) ? filter.aglomeracion : []).includes(String(a.id)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                aglomeracion: newValue.map(a => a.id)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Aglomeración" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingAglomeracions)} />
                                        )}
                                        noOptionsText={loadingAglomeracions ? optionsLoadingText : "Non se atoparon aglomeracións"}
                                    />
                                </Box>
                                <Box sx={{flex: "1 1 180px", maxWidth: {xs: "100%", sm: "240px"}}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>EDAR</InputLabel>
                                        <Select
                                            value={filter.edar}
                                            label="EDAR"
                                            onChange={e => setFilter({...filter, edar: e.target.value})}
                                        >
                                            <MenuItem value="">Todas</MenuItem>
                                            {edars.map(e => (
                                                <MenuItem key={e.id} value={e.id}>
                                                    {e.nome}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                        {(entityType === "bombeo" || entityType === "tanque") && (
                            <>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingAglomeracions}
                                        loadingText={optionsLoadingText}
                                        options={aglomeracions}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={aglomeracions.filter(a => (Array.isArray(filter.aglomeracion) ? filter.aglomeracion : []).includes(String(a.id)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                aglomeracion: newValue.map(a => a.id)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Aglomeración" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingAglomeracions)} />
                                        )}
                                        noOptionsText={loadingAglomeracions ? optionsLoadingText : "Non se atoparon aglomeracións"}
                                    />
                                </Box>
                                <Box sx={{flex: "1 1 240px", minWidth: "220px", maxWidth: {xs: "100%", sm: "320px"}}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        loading={loadingConcellos}
                                        loadingText={optionsLoadingText}
                                        options={concellos}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={concellos.filter(c => (Array.isArray(filter.concello) ? filter.concello : []).includes(String(c.codigo)))}
                                        onChange={(event, newValue) => {
                                            setFilter(prev => ({
                                                ...prev,
                                                concello: newValue.map(c => c.codigo)
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Concello" variant="outlined" size="small" placeholder="Seleccionar..." InputProps={withOptionsLoading(params, loadingConcellos)} />
                                        )}
                                        noOptionsText={loadingConcellos ? optionsLoadingText : "Non se atoparon concellos"}
                                    />
                                </Box>
                            </>
                        )}
                    </Stack>
                </Stack>

                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", my: 5}}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{border: "1px solid #eee"}}>
                        <Table sx={{minWidth: columns.length > 7 ? 1500 : 650}}>
                            <EntityTableSortingHead
                                order={order}
                                attribute={sort}
                                onRequestSort={handleRequestSort}
                                headCells={[
                                    ...columns.map(col => ({
                                        ...col,
                                        id: col.id_sort || col.id
                                    })),
                                    {id: "dummy"}
                                ]}
                            />
                            <TableBody>
                                {elements.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" sx={{py: 3}}>
                                            Non se atoparon elementos.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    elements.map((row, idx) => (
                                        <TableRow key={idx} hover>
                                            {columns.map(col => (
                                                <TableCell key={col.key}>
                                                    {col.format ? col.format(row[col.id]) || "-" : (row[col.id] ?? "-")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Grid container justifyContent="space-between" alignItems="center" sx={{mt: 3}}>
                    <Grid item>
                        <TableDownload
                            service={{
                                getList: (f, s, o, format) => {
                                    const apiFilter = {
                                        search: f.search,
                                        export_columns: columns.map(col => col.id).join(","),
                                    };
                                    if (entityType === "concello") {
                                        if (Array.isArray(f.provincia) && f.provincia.length > 0) {
                                            apiFilter.provincia = f.provincia.join(",");
                                        } else if (typeof f.provincia === "string" && f.provincia) {
                                            apiFilter.provincia = f.provincia;
                                        }
                                        if (f.poboacionFixaTotalRange) {
                                            const [min, max] = f.poboacionFixaTotalRange.split("-");
                                            if (min) apiFilter.poboacion_fixa_total_min = min;
                                            if (max) apiFilter.poboacion_fixa_total_max = max;
                                        }
                                        if (f.poboacionFixaSaneamentoRange) {
                                            const [min, max] = f.poboacionFixaSaneamentoRange.split("-");
                                            if (min) apiFilter.poboacion_fixa_saneamento_min = min;
                                            if (max) apiFilter.poboacion_fixa_saneamento_max = max;
                                        }
                                    }
                                    if (entityType === "aglomeracion") {
                                        if (f.poboacionTotalRange) {
                                            const [min, max] = f.poboacionTotalRange.split("-");
                                            if (min) apiFilter.poboacion_total_min = min;
                                            if (max) apiFilter.poboacion_total_max = max;
                                        }
                                        if (f.poboacionConectadaRange) {
                                            const [min, max] = f.poboacionConectadaRange.split("-");
                                            if (min) apiFilter.poboacion_conectada_min = min;
                                            if (max) apiFilter.poboacion_conectada_max = max;
                                        }
                                        if (f.poboacionFixaRange) {
                                            const [min, max] = f.poboacionFixaRange.split("-");
                                            if (min) apiFilter.poboacion_fixa_min = min;
                                            if (max) apiFilter.poboacion_fixa_max = max;
                                        }
                                        if (f.poboacionIndustrialRange) {
                                            const [min, max] = f.poboacionIndustrialRange.split("-");
                                            if (min) apiFilter.poboacion_industrial_min = min;
                                            if (max) apiFilter.poboacion_industrial_max = max;
                                        }
                                        if (f.tratamento) {
                                            apiFilter.tratamento = f.tratamento;
                                        }
                                    }
                                    if (entityType === "edar") {
                                        if (f.aglomeracion) apiFilter.aglomeracion = f.aglomeracion;
                                        if (f.concello) apiFilter.concello = f.concello;
                                        if (f.poboacionTotalRange) {
                                            const [min, max] = f.poboacionTotalRange.split("-");
                                            if (min) apiFilter.poboacion_total_min = min;
                                            if (max) apiFilter.poboacion_total_max = max;
                                        }
                                        if (f.poboacionConectadaRange) {
                                            const [min, max] = f.poboacionConectadaRange.split("-");
                                            if (min) apiFilter.poboacion_conectada_min = min;
                                            if (max) apiFilter.poboacion_conectada_max = max;
                                        }
                                        if (f.poboacionFixaRange) {
                                            const [min, max] = f.poboacionFixaRange.split("-");
                                            if (min) apiFilter.poboacion_fixa_min = min;
                                            if (max) apiFilter.poboacion_fixa_max = max;
                                        }
                                        if (f.poboacionIndustrialRange) {
                                            const [min, max] = f.poboacionIndustrialRange.split("-");
                                            if (min) apiFilter.poboacion_industrial_min = min;
                                            if (max) apiFilter.poboacion_industrial_max = max;
                                        }
                                        if (f.tratamento) {
                                            apiFilter.tratamento = f.tratamento;
                                        }
                                    }
                                    if (entityType === "conexion") {
                                        apiFilter.tipo_orixe = "nucleo";
                                        apiFilter.tipo_destino = "edar";
                                        if (f.concello) apiFilter.concello_orixe = f.concello;
                                        if (f.aglomeracion) apiFilter.aglomeracion = f.aglomeracion;
                                        if (f.edar) apiFilter.id_destino = f.edar;
                                    }
                                    if (entityType === "bombeo" || entityType === "tanque") {
                                        if (Array.isArray(f.aglomeracion) && f.aglomeracion.length > 0) {
                                            apiFilter.aglomeracion = f.aglomeracion.join(",");
                                        } else if (typeof f.aglomeracion === "string" && f.aglomeracion) {
                                            apiFilter.aglomeracion = f.aglomeracion;
                                        }

                                        if (Array.isArray(f.concello) && f.concello.length > 0) {
                                            apiFilter.concello = f.concello.join(",");
                                        } else if (typeof f.concello === "string" && f.concello) {
                                            apiFilter.concello = f.concello;
                                        }
                                    }

                                    switch(entityType) {
                                        case "concello": return DataVisualizationService.getConcellos(apiFilter, null, s, o, format);
                                        case "aglomeracion": return DataVisualizationService.getAglomeracions(apiFilter, null, s, o, format);
                                        case "edar": return DataVisualizationService.getEdars(apiFilter, null, s, o, format);
                                        case "conexion": return DataVisualizationService.getConexiones(apiFilter, null, s, o, format);
                                        case "bombeo": return DataVisualizationService.getBombeos(apiFilter, null, s, o, format);
                                        case "tanque": return DataVisualizationService.getTanques(apiFilter, null, s, o, format);
                                        default: return Promise.resolve([]);
                                    }
                                }
                            }}
                            filter={filter}
                            sort={sort}
                            order={order}
                            formats={[ServiceRequestFormat.CSV]}
                        />
                    </Grid>
                    <Grid item>
                        <Pagination
                            count={Math.ceil(total / pageSize)}
                            page={page}
                            onChange={(e, v) => updateQuery({page: v})}
                            color="primary"
                        />
                    </Grid>
                </Grid>
                </PaperContainer>
                </PageLayout>
                );
};

const DataDashboardContainer = () => {
    return (
        <DomainProvider>
            <DataDashboard />
        </DomainProvider>
    );
};

export default DataDashboardContainer;

